// Google Analytics 4 설정 및 이벤트 추적 유틸리티

// GTM dataLayer 이벤트 타입 정의
interface GTMEvent {
  event: string;
  event_category?: string;
  event_label?: string;
  value?: number;
  button_name?: string;
  link_text?: string;
  link_url?: string;
  section_name?: string;
  platform?: 'ios' | 'android';
  question?: string;
  notice_title?: string;
  language?: string;
  depth?: number;
  page_path?: string;
  page_title?: string;
  [key: string]: string | number | undefined;
}

// gtag 함수 매개변수 타입 정의
type GtagCommand = 'config' | 'event' | 'set' | 'get';
type GtagConfig = {
  page_title?: string;
  page_location?: string;
  page_path?: string;
  [key: string]: string | number | boolean | undefined;
};

declare global {
  interface Window {
    gtag: (command: GtagCommand, targetId: string, config?: GtagConfig) => void;
    dataLayer: GTMEvent[];
  }
}

export const GA_TRACKING_ID = 'G-N4XGJ1CQVH';
export const GTM_ID = 'GTM-PB4K7R9G';

// GTM을 통한 GA4 초기화 (GTM에서 GA4를 관리하므로 직접 로드하지 않음)
export const loadGA = () => {
  if (typeof window === 'undefined') return;

  // GTM이 로드되었는지 확인
  if (typeof window.gtag === 'function') {
    // GTM을 통해 GA4 설정 (GTM에서 GA4 태그가 설정되어 있어야 함)
    window.gtag('config', GA_TRACKING_ID, {
      page_title: document.title,
      page_location: window.location.href,
    });
  }
};

// 페이지뷰 추적
export const trackPageView = (url: string, title?: string) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('config', GA_TRACKING_ID, {
    page_path: url,
    page_title: title || document.title,
  });
};

// 이벤트 추적
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value: value,
  });
};

// 커스텀 이벤트 추적 (GA4 방식)
export const trackCustomEvent = (
  eventName: string,
  parameters?: GtagConfig
) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', eventName, parameters || {});
};

// GTM을 통한 이벤트 추적
export const trackGTMEvent = (
  eventName: string,
  parameters?: Omit<GTMEvent, 'event'>
) => {
  if (typeof window === 'undefined' || !window.dataLayer) return;

  window.dataLayer.push({
    event: eventName,
    ...parameters
  });
};

// 주요 이벤트들 (GTM dataLayer를 통한 추적)
export const GA_EVENTS = {
  // 버튼 클릭
  BUTTON_CLICK: (buttonName: string) => {
    trackGTMEvent('button_click', {
      event_category: 'engagement',
      button_name: buttonName
    });
  },

  // 링크 클릭
  LINK_CLICK: (linkText: string, linkUrl: string) => {
    trackGTMEvent('link_click', {
      event_category: 'engagement',
      link_text: linkText,
      link_url: linkUrl
    });
  },

  // 섹션 뷰
  SECTION_VIEW: (sectionName: string) => {
    trackGTMEvent('section_view', {
      event_category: 'engagement',
      section_name: sectionName
    });
  },

  // 다운로드 시작
  DOWNLOAD_START: (platform: 'ios' | 'android') => {
    trackGTMEvent('download_start', {
      event_category: 'conversion',
      platform
    });
  },

  // 문의하기
  CONTACT_START: () => {
    trackGTMEvent('contact_start', {
      event_category: 'conversion'
    });
  },

  // FAQ 조회
  FAQ_VIEW: (question: string) => {
    trackGTMEvent('faq_view', {
      event_category: 'engagement',
      question
    });
  },

  // 공지사항 조회
  NOTICE_VIEW: (noticeTitle: string) => {
    trackGTMEvent('notice_view', {
      event_category: 'engagement',
      notice_title: noticeTitle
    });
  },

  // 언어 변경
  LANGUAGE_CHANGE: (language: string) => {
    trackGTMEvent('language_change', {
      event_category: 'engagement',
      language
    });
  },

  // 스크롤 깊이
  SCROLL_DEPTH: (depth: number) => {
    trackGTMEvent('scroll_depth', {
      event_category: 'engagement',
      depth
    });
  },
} as const;
